import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Dialog,
  Fab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { Theme, useTheme } from "@mui/material/styles";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import SnackbarMessage from "components/SnackbarMessage";
import { format } from "date-fns";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  ResponseOfResourceLibraryDetail,
  ResponseOfResourceLibraryItem,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import ResourceLibraryItemTopicsList from "./ResourceLibraryItemTopicsList";
import ResourceLibraryItemProfessionsList from "./ResourceLibraryItemProfessionsList";
import ResourceLibraryItemUpdateForm from "./ResourceLibraryItemUpdateForm";
import useTitle from "hooks/useTitle";

const useStyles = (theme: Theme) => ({
  tableTitle: {
    margin: "20px 0",
  },
});

const ResourceLibraryItemDetails = () => {
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { registerPromise } = useTrashablePromises();
  const navigate = useNavigate();
  const styles = useStyles(useTheme());
  const { id, rid } = useParams<{ id: string; rid: string }>();
  const {
    resourceLibraryService,
    resourceLibrariesUrl,
    resourceLibrariesDetailsUrl,
  } = useGlobalContext();
  const [updateOpen, updateOpenActions] = useBoolean(false);
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);

  const resourceLibraryItemResult = useQuery<ResponseOfResourceLibraryItem>(
    ["resourceLibraryItem", id],
    () => resourceLibraryService.getResourceLibraryItemById(id!),
    {
      enabled: !!id,
      refetchOnMount: "always",
    },
  );

  const resourceLibraryResult = useQuery<ResponseOfResourceLibraryDetail>(
    ["resourceLibrary", rid],
    () => resourceLibraryService.getResourceLibraryById(rid!),
    {
      enabled: !!rid,
    },
  );

  const resourceLibraryItem = resourceLibraryItemResult.data?.data;
  const resourceLibrary = resourceLibraryResult.data?.data;

  useTitle(resourceLibraryItem?.name || "Resource Library Item Details");

  if (!id) return <Navigate to={resourceLibrariesUrl()} replace />;
  if (resourceLibraryItemResult.isLoading || resourceLibraryResult.isLoading)
    return <Loader />;
  if (resourceLibraryItemResult.isError || resourceLibraryResult.isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="resourceLibraryItemLoadError" />
        </Alert>
      </Box>
    );
  }

  const onResourceLibraryItemUpdated = () => {
    queryClient.invalidateQueries("resourceLibraryItem");
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "resourceLibraryItemUpdated" })),
    );
    updateOpenActions.setFalse();
  };

  const handleDelete = async () => {
    await registerPromise(resourceLibraryService.deleteResourceLibraryItem(id));
    queryClient.invalidateQueries("resourceLibraryItems");
    navigate(resourceLibrariesDetailsUrl(resourceLibrary?.id));
  };

  const rows = [
    {
      title: t({ id: "resourceLibraryDetailsNameLabel" }),
      component: resourceLibraryItem?.name && (
        <span>{resourceLibraryItem.name}</span>
      ),
    },
    {
      title: t({ id: "resourceLibraryDetailsLibraryNameLabel" }),
      component: <span>{resourceLibrary?.name}</span>,
    },
    {
      title: t({ id: "publicationDate" }),
      component: resourceLibraryItem?.publicationDate && (
        <span>{format(resourceLibraryItem.publicationDate, "P")}</span>
      ),
    },
    {
      title: t({ id: "resourceLibraryDetailsTypeLabel" }),
      component: resourceLibraryItem?.resourceLibraryTypeName && (
        <span>{resourceLibraryItem.resourceLibraryTypeName}</span>
      ),
    },
    {
      title: t({ id: "descriptionLabel" }),
      component: resourceLibraryItem?.description && (
        <span>{resourceLibraryItem.description}</span>
      ),
    },
    {
      title: t({ id: "fileNameLabel" }),
      component: resourceLibraryItem?.fileName && (
        <a
          target="_blank"
          href={resourceLibraryItem.contentUrl}
          rel="noreferrer"
        >
          {resourceLibraryItem.fileName}
        </a>
      ),
    },
    {
      title: t({ id: "resourceLibraryDetailsSequenceLabel" }),
      component: resourceLibraryItem?.sequence && (
        <span>{resourceLibraryItem.sequence}</span>
      ),
    },
  ].filter((r) => !!r.component);

  return (
    <>
      <RedirectBackButton
        redirectRoute={resourceLibrariesDetailsUrl(rid)}
        labelKey="backToDetailsButtonLabel"
      />
      <h1>{resourceLibraryItem?.name}</h1>
      <Box mb={3} display="flex" justifyContent="center">
        <Fab color="primary" onClick={updateOpenActions.setTrue}>
          <EditIcon />
        </Fab>
        <Box ml={4} />
        <Fab color="secondary" onClick={deleteOpenActions.setTrue}>
          <DeleteIcon />
        </Fab>
      </Box>
      <Typography variant="h5" sx={styles.tableTitle}>
        Resource Library Item
      </Typography>
      <TableContainer>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell component="th" scope="row">
                  <b>{title}</b>
                </TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ResourceLibraryItemTopicsList
        resourceLibraryItem={resourceLibraryItem!}
      />
      <ResourceLibraryItemProfessionsList
        resourceLibraryItem={resourceLibraryItem!}
      />

      <Dialog open={updateOpen} disableEnforceFocus>
        <ResourceLibraryItemUpdateForm
          resourceLibraryItem={resourceLibraryItem!}
          resourceLibrary={resourceLibrary}
          onResourceLibraryItemUpdated={onResourceLibraryItemUpdated}
          onCancel={updateOpenActions.setFalse}
        />
      </Dialog>

      <DeleteEntityDialog
        dialogTitleKey="resourceLibraryItemDeleteTitle"
        contentKey="resourceLibraryItemDeleteInfo"
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default ResourceLibraryItemDetails;
